import React, { useEffect, useRef, useCallback } from 'react';
import { connect, useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import { changeFormField, setNewForm, updateFields, changeFormFieldValue, updateFormProperties, removeFields } from 'apps/auth/actions/formAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import PreCheckPopup from 'apps/auth/components/common/send/popups/Precheck/PreCheckPopup';
import Form from 'apps/auth/components/form-multi/Form';
import { getField } from 'apps/auth/components/form-multi/utils';
import OnboardingQuestionnaireModal from 'apps/auth/components/send/modals/OnboardingQuestionnaireModal';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import { SEND_FORM_NAME } from 'apps/auth/constants/formName';
import { AUTH_SEND_PLAID_PAY_CREATE } from 'apps/auth/constants/UrlConstants';
import { showErrorType } from 'apps/auth/helpers/utils';
import { useApp, useExtra, useForm } from 'apps/auth/hooks/selectors';

import PlaidBankList from './PlaidBankList';

const FORM_NAME = 'searchPlaidBank';

const PlaidBankModal = ({
    modalRef,
    actualRef,
    setNewForm,
    updateFields,
    formName,
    changePaymentMethod,
    changeFormFieldValue,
    updateFormProperties,
    removeFields,
    popupRef,
    typeOfTransfer,
    onCloseModal,
}) => {
    const plaidForm = useForm(formName);
    const submitPayRef = useRef(null);
    const searchField = useSelector(({ form }) => form?.[FORM_NAME]?.fields?.search);
    const searchPlaceholder = useSelector(({ messages }) => messages?.pageMessages?.searchPlaceholder);
    const { errors, messageBody } = useApp();
    const { useQuestionnaireScreen, questionnaire } = useExtra() ?? {};
    const softOrHardError = showErrorType(messageBody, errors);
    let postfix = '';
    if (typeOfTransfer) {
        postfix += `?type=${typeOfTransfer}`;
    }

    useEffect(() => {
        if (!searchField) {
            const search = getField('search', '', '', false, false, searchPlaceholder, 'search');
            setNewForm(FORM_NAME, { [search.name]: search });
        }
        if (plaidForm) {
            const bankId = getField('bankId', '', '', false, false, '', 'text', true, true);
            updateFields(formName, { [bankId.name]: bankId });
        }
    }, [formName, searchPlaceholder, setNewForm, updateFields, updateFormProperties]);

    const onSubmitHandler = (fields, json) => {
        if (!softOrHardError && json?.extra?.precheck) {
            const openPrecheckPopup = () => {
                popupRef?.current?.open(<PreCheckPopup popupRef={popupRef} formName={SEND_FORM_NAME} />);
            };
            if (useQuestionnaireScreen && questionnaire?.fields?.length > 0) {
                modalRef?.current?.open(<OnboardingQuestionnaireModal modalRef={modalRef} onSubmitSuccess={openPrecheckPopup} />);
                return;
            }
            modalRef?.current?.close();
            openPrecheckPopup();
        } else {
            modalRef?.current?.close();
        }
    };

    const onClickHandler = useCallback(
        (bankId) => {
            changeFormFieldValue(formName, bankId, 'bankId')
                .then(() => updateFormProperties(formName, null, false))
                .then(() => submitPayRef?.current.click())
                .finally(() => removeFields(formName, ['bankId']));
        },
        [changeFormFieldValue, formName, updateFormProperties],
    );

    const onCloseIconClick = (e) => {
        e?.preventDefault?.();
        onCloseModal?.();
        modalRef?.current?.close();
    };

    return (
        <ModalComponent isIconClose modalRef={modalRef} onCloseIconClick={onCloseIconClick}>
            <PlaidBankList
                popupRef={popupRef}
                actualRef={actualRef}
                modalRef={modalRef}
                submitPayRef={submitPayRef}
                searchField={searchField}
                onClickHandler={onClickHandler}
                changePaymentMethod={changePaymentMethod}
            />
            <Form
                formName={formName}
                action={AUTH_SEND_PLAID_PAY_CREATE + postfix}
                onSubmitHandler={onSubmitHandler}
                validateForm={false}
                getCanonicalPath={true}
            >
                <Button type="submit" raised={true} variant={'hidden'} ref={submitPayRef} />
            </Form>
        </ModalComponent>
    );
};

PlaidBankModal.propTypes = {
    modalRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
    popupRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
    actualRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
    formName: PropTypes.string,
    changePaymentMethod: PropTypes.func,
    onCloseModal: PropTypes.func,
};

export default connect(null, {
    setNewForm,
    updateFields,
    changeFormField,
    changeFormFieldValue,
    updateFormProperties,
    removeFields,
})(PlaidBankModal);
